import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const Chakrata = () => {
  const tourData = {
    name: "Chakrata Tiger Waterfall",
    duration: "02 Nights 03 Days",
    price: "₹ 4,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935198/thfz6pxxfavwgzoixjqo.jpg",
    altitude: "7,000 ft",
    difficulty: "Easy",
    region: "Uttarakhand",
    highlights: ["Weekend getaway", "Tiger Waterfall", "Mountain views"],
    bestTime: "March to November",
    description: "The Chakrata Tiger Waterfall tour is a perfect weekend getaway in Uttarakhand that combines adventure with scenic beauty. This easy tour offers waterfall visits, cave trekking, and mountain views, making it ideal for families and adventure enthusiasts."
  };

  return (
    <>
      <Helmet>
        <title>Chakrata Tiger Waterfall Tour - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Chakrata Tiger Waterfall tour in Uttarakhand. Weekend getaway with waterfall visits and scenic beauty." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Weekend getaway with waterfall visits and scenic beauty
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This tour takes you to the beautiful hill station of Chakrata and the 
                    stunning Tiger Waterfall, offering a perfect blend of adventure and relaxation. 
                    The journey includes cave trekking, waterfall visits, and scenic mountain views, 
                    making it an ideal weekend escape from city life.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Weekend getaway experience</li>
                    <li>Tiger Waterfall visit</li>
                    <li>Bhuder Cave trek</li>
                    <li>Mountain views</li>
                    <li>Chirmiri Neck Top</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for families</li>
                    <li>Bonfire and music</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 0: Departure from Delhi in Night</h4>
                    <ul>
                      <li>Board the Bus/Traveler from Mayur Vihar Extension metro station, Delhi</li>
                      <li>Reporting - 11:00 pm</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 1: Reach Chakrata | Bhuder Cave Trek</h4>
                    <ul>
                      <li>Reach Chakrata in morning. Check-in the resort, rest for a while</li>
                      <li>After some rest, drive towards Budher cave (a hike of 5km)</li>
                      <li>In evening, return back to resort</li>
                      <li>In evening enjoy bonfire with snacks and some music</li>
                      <li>Conclude with dinner and overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Visit to Tiger Waterfall & Chirmiri Necktop</h4>
                    <ul>
                      <li>After breakfast, check out from resort and visit highest waterfalls in Uttarakhand, known as Tiger Falls</li>
                      <li>Later, move towards Chirmiri Neck Top, enjoy scenic sunset</li>
                      <li>Afterwards depart for Delhi</li>
                      <li>Reach Delhi by early morning</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Transportation from Delhi to Delhi: comfortable AC Deluxe Bus/Tempo Traveler</li>
                    <li>1 Night Accommodation on sharing basis: Resort/Cottages</li>
                    <li>3 Meals: Dinner (1), Breakfast (1) & Evening Snacks (1), starting from Evening Snacks on Day 1 to Breakfast on Day 2</li>
                    <li>Bonfire (if weather permits) & music during the stay and local explorations as per itinerary</li>
                    <li>Experienced and cool Trip Captain</li>
                    <li>Driver allowances, toll taxes, parking, state taxes</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Any meal/food item other than the package inclusions</li>
                    <li>Any entry ticket to monuments & public places</li>
                    <li>Charges of videos/photos during Paragliding</li>
                    <li>Any expense that is not mentioned in the package inclusion</li>
                    <li>Any extra cost that occurs due to natural calamity or any such unseen situation that is beyond control</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>7,000 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Tour Distance:</strong>
                      <span>280 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>March to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person</h4>
                  <ul>
                    <li><strong>QUAD SHARING:</strong> ₹ 4,999 + 5% GST</li>
                    <li><strong>TRIPLE SHARING:</strong> ₹ 5,499 + 5% GST</li>
                    <li><strong>DOUBLE SHARING:</strong> ₹ 5,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> ISBT Kashmere Gate b/w 09:00 PM to 10:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Weekend getaway experience</li>
                    <li>Tiger Waterfall visit</li>
                    <li>Bhuder Cave trek</li>
                    <li>Mountain views</li>
                    <li>Chirmiri Neck Top</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for families</li>
                    <li>Bonfire and music</li>
                    <li>Trip captain support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Chakrata;
